import { memo, useEffect, useState } from "react";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useNavigate } from "react-router-dom";
import {
  AuraLayout,
  Button,
  Layout,
  SupportV2Icon,
} from "lola-framework-ui-test";
import { BodyCopy } from "../components/BodyCopy";
import { useTranslation } from "react-i18next";
import { APP_ROUTES, flavorClassName } from "../constants";
// import { TransferPanel } from "../components/TransferPanel";
// import { FlagCircle } from "../components/FlagCircle";
import { TransactionsHistory } from "../components/TransactionsHistory";
import { useOperationSSE } from "../hooks/useOperationSSE";
import { listOperations } from "../services/api";
import type { ITransaction } from "../interfaces/transactions";
import { TransactionsStatus } from "../utils/commons";
import "../styles/transactionspage.style.css";
import useRequest from "../hooks/useRequest";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useClaimLink } from "../hooks/useClaimLink";
import { getEvents, safeCapture } from "../analytics";
import { NotificationsBell } from "../components/notifications/NotificationsBell";
import { useNovuInbox } from "../components/notifications/NovuInboxProvider";
import { Navbar } from "../components/Navbar";
// import { getActiveCorridor } from "../utils/catalog";

const formatDate = (isoDate: string): string => {
  const date = new Date(isoDate);
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear().toString().slice(-2);
  return `${day}/${month}/${year}`;
};

const mapOperationStatusToTransactionStatus = (
  status: string
): TransactionsStatus => {
  switch (status) {
    case "pending":
    case "created":
    case "processing":
      return TransactionsStatus.PENDING;
    case "completed":
      return TransactionsStatus.COMPLETED;
    case "failed":
      return TransactionsStatus.ERROR;
    case "cancelled":
    case "canceled":
      return TransactionsStatus.CANCELLED;
    default:
      return TransactionsStatus.PENDING;
  }
};

export const TransactionsPage = memo(() => {

  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("home"));
  }, []);
  useClaimLink();
  const { t } = useTranslation();
  const { theme, setTransactions, setOperations, operations } = useStore();
  const { session } = useSessionStore();
  const navigate = useNavigate();
  const { redirectTo } = useRouteWithToken();
  const colorConfig = theme.whiteView;
  const { backgroundBtn, textColorBtn, dropzoneColors, bodyCopy } = colorConfig;
  const userStatus = session.data?.userStatus;
  const canSend = !userStatus?.blocked && !userStatus?.suspended;

  const goToTransfer = () => {
    if (!canSend) return;
    redirectTo(APP_ROUTES.TRANSFER_PAGE);
  };
  const [transactions, setLocalTransactions] = useState<ITransaction[]>([]);

  useOperationSSE(session.data?.linkId);

  // // Get currencies from session's active corridor
  // const activeCorridor = getActiveCorridor(session.data?.config);
  // const sourceCurrency =
  //   COUNTRY_CURRENCY[activeCorridor?.originCountryCode || "US"] || "USD";
  // const destinationCurrency =
  //   COUNTRY_CURRENCY[activeCorridor?.destinationCountryCode || "MX"] || "MXN";

  // // Fetch exchange rate from API
  // const { rate, loading: rateLoading } = useExchangeRate(
  //   sourceCurrency,
  //   destinationCurrency
  // );

  // // Format the exchange rate for display
  // const displayRate = rateLoading ? "..." : rate?.toString() || "--";

  const handleFetchOperations = async () => {
    const linkId = session.data?.linkId;
    if (!linkId) return;

    const fetchOperations = async () => {
      try {
        const ops = await listOperations(linkId);
        setOperations(ops);
      } catch (error) {
        console.error("Failed to fetch operations:", error);
      }
    };

    void fetchOperations();
    return;
  };
  useRequest(handleFetchOperations, [session.data?.linkId, setOperations]);

  const handleFetchTransactions = async () => {
    if (!session.data?.linkId) return;

    const mappedTransactions: ITransaction[] = operations.map((op) => ({
      operationId: op.operationId,
      status: mapOperationStatusToTransactionStatus(op.status),
      amount: op.receiveAmount.toFixed(2),
      currency: op.destinationCurrency,
      date: formatDate(op.createdAt),
    }));

    // TODO: check this
    setLocalTransactions(mappedTransactions);
    setTransactions(mappedTransactions);
    return;
  };
  useRequest(handleFetchTransactions, [operations, session.data?.linkId]);

  const inbox = useNovuInbox();
  const headerSlot = inbox?.headerSlot ?? "help";
  const showBell = headerSlot === "bell";

  return (
    <AuraLayout
      colorConfig={colorConfig}
      className={`white-view-background ${flavorClassName}`}
    >
      <Layout.Header>
        <Navbar
          title={t("transactions.nav.title")}
          align="center"
          onBackClick={() => navigate(-1)}
          icon={
            showBell ? (
              <span className="m2m-notifications-bell-slot">
                <NotificationsBell />
              </span>
            ) : headerSlot === "pending" ? undefined : (
              <SupportV2Icon className="inverted-icon" colors={dropzoneColors} />
            )
          }
          onIconClick={
            showBell || headerSlot === "pending"
              ? undefined
              : () => redirectTo(APP_ROUTES.SUPPORT_PAGE)
          }
        />
      </Layout.Header>
      <Layout.Content className={`transactions-section ${flavorClassName}`}>
        {/* <section className="transfer-panel-container">
          <TransferPanel
            amount={displayRate}
            currency={destinationCurrency}
            footerText={t("transactions.currentRate")}
            icon={
              <FlagCircle
                iso2={(
                  activeCorridor?.destinationCountryCode || "MX"
                ).toLowerCase()}
              />
            }
          />
        </section> */}
        <TransactionsHistory items={transactions.slice(0, 5)} />
      </Layout.Content>
      <Layout.Footer>
        <section className="footer-section transactions-footer">
          {!canSend && (
            <BodyCopy
              textColor={bodyCopy}
              className="transactions-disabled-note"
            >
              {t("transactions.transfersDisabled")}
            </BodyCopy>
          )}
          <Button
            size="small"
            variant="cancel"
            background={backgroundBtn}
            color={backgroundBtn}
            className="alreary-acc-btn"
            disabled={!canSend}
            onClick={goToTransfer}
          >
            {t("transactions.backHome")}
          </Button>
          <Button
            size="small"
            background={backgroundBtn}
            color={textColorBtn}
            disabled={!canSend}
            onClick={goToTransfer}
          >
            {t("transactions.transfer")}
          </Button>
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});
